/**
 * `queue.ts` — the localStorage-backed outbox for client crash reports.
 *
 * The queue is what makes the feature survive the conditions it exists to observe. An error
 * worth reporting often arrives exactly when the app can't talk to the server — mid-deploy,
 * on a dropped wifi, or because the crash *is* the connection dying — so a reporter that only
 * POSTs live would lose precisely the reports that mattered. Every capture goes to disk first;
 * delivery is a separate, retryable step.
 *
 * ── EVERY OPERATION HERE IS FAILURE-TOLERANT, ON PURPOSE ──
 *
 * `localStorage` is not a reliable API. It throws on quota exhaustion, it throws under Safari
 * private browsing, it throws when a browser blocks storage for the origin, and its contents
 * can be edited or corrupted by hand. This module therefore never propagates a failure: a
 * broken store degrades to "no queue" and the app keeps running. Crash reporting that can
 * itself crash the app is worse than no crash reporting — and it fails in the same conditions
 * as the app, which is when it would do maximum damage.
 */
import { CLIENT_ERROR_QUEUE_CAP, type ClientErrorReport } from "@lancer-vtt/shared";
import { storageGetJson, storageRemove, storageSetJson } from "../browserStorage";

/**
 * Versioned key. If the report shape ever changes, bump it rather than writing a reader that
 * tolerates both — per the repo's no-backward-compat default, an unreadable old queue is
 * simply dropped, and losing a handful of undelivered crash reports across a schema change
 * costs nothing worth the migration code.
 */
const STORAGE_KEY = "lvtt.clientErrors.v1";

/** Read the queue. Any corruption (bad JSON, wrong shape, hand-edit) reads as empty. */
export function readQueue(): ClientErrorReport[] {
  // Unreachable store, unset key, unparseable blob and a non-array payload all read as empty.
  const read = storageGetJson<unknown>(STORAGE_KEY);
  if (!read.ok || !Array.isArray(read.value)) return [];
  return read.value.filter((r): r is ClientErrorReport => typeof r === "object" && r !== null && typeof (r as ClientErrorReport).message === "string");
}

/**
 * Replace the queue. Swallows the write failure — a full or unavailable store means this
 * report is lost, which is the correct trade against throwing inside an error handler.
 */
export function writeQueue(reports: ClientErrorReport[]): void {
  // A rejected write is dropped on the floor: quota exceeded / storage blocked / no store
  // at all. There is nothing useful to do about it from inside an error path.
  if (reports.length === 0) storageRemove(STORAGE_KEY);
  else storageSetJson(STORAGE_KEY, reports);
}

/**
 * The identity two reports are considered "the same" by: source + message + the first real
 * stack **frame**. The message alone would collapse genuinely different
 * `undefined is not a function` bugs; the whole stack would split one bug into many when a
 * column shifts between renders.
 *
 * **"First real frame" is not "first line."** V8 prefixes a stack with a message header
 * (`TypeError: x is not a function`), so taking the first non-empty line picks up the header
 * — which is already in the key via `message` — and two unrelated bugs that share a message
 * would dedupe into one. Skip to the first line that actually looks like a frame.
 */
function identity(report: ClientErrorReport): string {
  const lines = report.stack?.split("\n") ?? [];
  const firstFrame = lines.find((l) => {
    const t = l.trim();
    return t !== "" && (t.startsWith("at ") || t.includes("@"));
  });
  return [report.source, report.message, firstFrame?.trim() ?? ""].join(" | ");
}

/**
 * Queue one report, deduped and bounded.
 *
 * **Dedupe keeps the FIRST occurrence, not the last.** A React component throwing on every
 * render produces hundreds of identical reports in seconds; without this, the queue is a
 * single bug repeated `CLIENT_ERROR_QUEUE_CAP` times and every *other* error that session has
 * been evicted. Keeping the first also keeps the earliest `capturedAt`, which is the one that
 * says when the trouble started.
 *
 * The known cost: the log records that a bug happened, not that it happened 400 times. That's
 * an acceptable trade for a review log — the repeat count is a metrics question, and this is
 * not a metrics system.
 */
export function enqueue(report: ClientErrorReport): void {
  const existing = readQueue();
  const key = identity(report);
  if (existing.some((r) => identity(r) === key)) return;
  // At the cap, drop the OLDEST. The alternative — refusing the new one — freezes the queue
  // on whatever filled it, so a later, more relevant crash could never be recorded.
  const next = [...existing, report].slice(-CLIENT_ERROR_QUEUE_CAP);
  writeQueue(next);
}

/** Drop the reports that were just delivered, preserving anything queued since the flush began. */
export function dropDelivered(delivered: ClientErrorReport[]): void {
  if (delivered.length === 0) return;
  const keys = new Set(delivered.map(identity));
  writeQueue(readQueue().filter((r) => !keys.has(identity(r))));
}
